import axios from 'axios';
import { ReduxStore } from '../redux-store/ReduxStore';
import { config } from './Config';
import { auth } from '../authentication/Firebase.jsx';
import { performSessionCleanup, safeRedirectToLogin } from '../authentication/sessionCleanup.js';
import {
    isNetworkAuthError, isReauthPending, requestReauth, waitForReauth,
} from '../authentication/sessionExpiry.js';
import {
    gameAccessRefusal, notifyGameAccessRefused, refusedGameOf, rememberRefusedGame, forgetRefusedGames, defaultGameHeader,
} from '../utils/gamesChanged.js';
import { isClosedScopeRead, notifyClosedScopeRead } from '../utils/gameLifecycle.js';
import { isRetryableFailure, isTransientReadFailure, reachedNoServer, retryAfterHeader } from './retryPolicy.js';
import { ACCESS_CHECK_EVENT, ACCESS_CHANGED_EVENT } from '../authentication/accessChange.js';
import { getAppStatus, markServerReachable, markServerUnreachable, parseRetryAfter } from '../shell/app-status/appStatus.js';

// Whose refusals apply: the signed-in user (see defaultGameHeader).
const signedInUserKey = () => auth.currentUser?.uid || auth.currentUser?.email || '';

// An owner may have reactivated the caller: send their selected game again and let the server decide.
if (typeof window !== 'undefined') {
    window.addEventListener(ACCESS_CHANGED_EVENT, () => forgetRefusedGames());
}

const MUTATING_METHODS = ['post', 'put', 'patch'];
const MAX_RETRIES = 3;
const RETRY_BASE_DELAY_MS = 1000;
// A GET that gets no answer at all would leave its page loading forever. Generous, because some reads wait on the AI.
const READ_TIMEOUT_MS = 90000;
// A GET that reached no server (a deploy restarting the pods, a dropped connection) is tried again twice: 1s, 2s.
const READ_RETRIES = 2;
// After a hard refresh Firebase restores the session a moment after the first requests go out. Wait for it (bounded),
// rather than send the persisted token, which is an hour old after a long sleep and earns a 401 nobody can refresh.
const AUTH_RESTORE_WAIT_MS = 5000;

let authRestorePromise = null;
const waitForAuthRestore = () => {
    if (typeof auth.authStateReady !== 'function') return Promise.resolve();
    if (!authRestorePromise) {
        authRestorePromise = Promise.race([
            auth.authStateReady(),
            new Promise((resolve) => setTimeout(resolve, AUTH_RESTORE_WAIT_MS)),
        ]).catch(() => {});
    }
    return authRestorePromise;
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * The session could not be refreshed: open the sign-in dialog (sessionExpiry.js) and send this request again once the
 * user is back. The page stays as it is meanwhile. If they leave for the sign-in page, the request fails as before.
 */
const replayAfterReauth = async (requestConfig, error) => {
    try {
        await requestReauth(auth.currentUser?.uid);
    } catch {
        return Promise.reject(error);
    }
    requestConfig.__authRetried = true;
    requestConfig.__reauthed = true;
    return axios(requestConfig);
};

/**
 * Shared Promise for in-flight token refreshes. When multiple concurrent
 * requests all receive 401 at once, they dedupe onto this single Promise
 * instead of each calling getIdToken(true) independently. Firebase also
 * dedupes internally at the network level, but this additionally collapses
 * the axios replay microtask chain.
 */
let refreshPromise = null;

/**
 * True if this request is targeting our backend API (by full URL or baseURL).
 */
function isBackendCall(requestConfig) {
    if (!config.serverBaseUrl || !requestConfig) return false;
    return (
        requestConfig.url?.startsWith(config.serverBaseUrl) ||
        requestConfig.baseURL?.includes(config.serverBaseUrl)
    );
}

/**
 * Token used during the Firebase rehydration window (hard refresh), when
 * {@code auth.currentUser} is briefly null. AuthContext persists
 * {@code {...user, accessToken}} under the {@code authUser} sessionStorage key
 * (and keeps it fresh via onIdTokenChanged), so we read the accessToken back as
 * a fallback. This lets the interceptor attach auth on the very first on-mount
 * fetch after a refresh — which is why feature services no longer need to set
 * the Authorization header themselves. A stale token here is still safe: the
 * response interceptor force-refreshes and replays on a 401.
 */
function readPersistedAuthToken() {
    try {
        const raw = sessionStorage.getItem('authUser');
        return raw ? (JSON.parse(raw)?.accessToken || null) : null;
    } catch {
        return null;
    }
}

/**
 * Axios request interceptor that automatically adds:
 *   1. A fresh Firebase ID token as Authorization: Bearer <token>. Because
 *      getIdToken() re-reads from Firebase on every call, the token can
 *      never be stale — Firebase internally caches it until ~5 min before
 *      expiry, then silently rotates via the refresh token. Callers no
 *      longer need to pass accessToken manually. Requests can opt out by
 *      setting `skipAuth: true` on the axios config (used by deliberately
 *      public endpoints).
 *   2. Org/game/client context headers to every outgoing backend request.
 *   3. An X-Idempotency-Key header on mutating requests (POST/PUT/PATCH)
 *      so the backend can deduplicate retried requests.
 *   4. A RETRY-ATTEMPT header when the request is a retry.
 */
axios.interceptors.request.use(async (requestConfig) => {
    // Only add headers for requests going to our backend
    if (isBackendCall(requestConfig)) {
        const state = ReduxStore.getState();
        const ctx = state?.dashboardContext;

        if (ctx) {
            // Tenant scope headers: the backend resolves scope from the game id.
            if (ctx.selectedOrgId) {
                requestConfig.headers['X-Web-Org-Id'] = ctx.selectedOrgId;
            }
            // Respect a game id the caller pre-set for this one request (e.g. listing another game's
            // segments, or onboarding threading the just-created game's id before the Redux scope
            // catches up — the active-game guard drops a game not yet in accessible-games); otherwise
            // fall back to the active game.
            // A selected game the server refused to open for this user (suspended on it) is not sent as the
            // default: every request would be refused with it, including the ones the header needs to move them
            // to a game they can open. Its segment goes with it.
            const defaultGameId = defaultGameHeader(signedInUserKey(), ctx.selectedGameId);
            if (defaultGameId && !requestConfig.headers['X-Web-Game-Id']) {
                requestConfig.headers['X-Web-Game-Id'] = defaultGameId;
            }
            if (ctx.selectedSegmentId && defaultGameId) {
                requestConfig.headers['X-Web-Segment-Id'] = ctx.selectedSegmentId;
            }
        }

        // Attach a fresh Firebase ID token on every outgoing backend request.
        // Skipped when:
        //   - playground mode is active (the playground interceptor swaps in
        //     a mock adapter downstream and never actually hits the network)
        //   - the caller explicitly opted out with { skipAuth: true }
        //   - the user is not logged in (preserves public-endpoint behavior
        //     on the login / signup / homepage flows)
        const isPlayground = sessionStorage.getItem('playgroundMode') === 'true';
        if (!isPlayground && !requestConfig.skipAuth) {
            try {
                // The sign-in dialog is up: wait for the user rather than collect a 401 per request.
                if (isReauthPending()) await waitForReauth().catch(() => {});
                if (!auth.currentUser) await waitForAuthRestore();
                if (auth.currentUser) {
                    // Fresh token from Firebase (also covers the ~55-min rotation).
                    requestConfig.headers.Authorization = `Bearer ${await auth.currentUser.getIdToken()}`;
                } else if (!requestConfig.headers.Authorization) {
                    // Hard-refresh window: auth.currentUser is briefly null while
                    // Firebase rehydrates, but on-mount fetches fire immediately.
                    // Fall back to the sessionStorage-persisted token so they don't
                    // 401. Centralizing this here is what lets feature services
                    // (Workflow/Question/SkillRegistry) drop their manual headers.
                    const persisted = readPersistedAuthToken();
                    if (persisted) requestConfig.headers.Authorization = `Bearer ${persisted}`;
                }
            } catch (err) {
                // Let the request proceed with whatever header is present. The
                // response interceptor will catch a 401 and force-refresh.
                console.error('axiosConfig: failed to attach auth token', err);
            }
        }

        const method = (requestConfig.method || '').toLowerCase();
        if (method === 'get' && !requestConfig.timeout) {
            requestConfig.timeout = READ_TIMEOUT_MS;
        }

        // Add idempotency key for mutating requests
        if (MUTATING_METHODS.includes(method) && !requestConfig.headers['X-Idempotency-Key']) {
            requestConfig.headers['X-Idempotency-Key'] =
                requestConfig.__idempotencyKey || crypto.randomUUID();
        }

        // Preserve the idempotency key for potential retries
        if (!requestConfig.__idempotencyKey) {
            requestConfig.__idempotencyKey = requestConfig.headers['X-Idempotency-Key'];
        }

        // Add retry attempt header if this is a retry
        if (requestConfig.__retryCount > 0) {
            requestConfig.headers['RETRY-ATTEMPT'] = String(requestConfig.__retryCount);
        }
    }
    return requestConfig;
});

/**
 * Axios response interceptor:
 *
 * 1. On 401 (regardless of HTTP method), force-refresh the Firebase ID
 *    token via getIdToken(true) and replay the original request exactly
 *    once. If the refresh fails for want of a network, the user stays
 *    signed in (the status bar says the connection is down). If it fails
 *    otherwise, or the retry still 401s, the sign-in dialog opens over the
 *    page and the request is sent again once the user is back
 *    (sessionExpiry.js). A request that 401s even after that ends the
 *    session: cleanup, then the sign-in page with a link back here. If
 *    auth.currentUser is null once Firebase has restored, the user is
 *    mid-logout or on a logged-out page — just reject.
 *
 * 2. On 5xx / 409 / network error for MUTATING requests, retry with
 *    exponential backoff (1s, 2s, 4s), reusing the same X-Idempotency-Key
 *    so the backend returns the cached response instead of re-executing.
 *    Max 3 retries.
 *
 * 3. A GET that reached no server (network error, 502/503/504) is tried
 *    twice more (1s, 2s). Still nothing: the "Can't reach GeoPlay" bar
 *    shows until the server answers again (appStatus.js).
 */
axios.interceptors.response.use(
    (response) => {
        if (getAppStatus().serverUnreachable && isBackendCall(response.config)) markServerReachable();
        return response;
    },
    async (error) => {
        const requestConfig = error.config;

        // Only handle errors from our backend
        if (!requestConfig || !isBackendCall(requestConfig)) {
            return Promise.reject(error);
        }

        const status = error.response?.status;

        // ── 401 auth refresh & retry ─────────────────────────────────
        if (status === 401) {
            if (requestConfig.__reauthed) {
                // Signed in again and still refused: no dialog can fix this (account disabled
                // server-side, clock skew, backend broken). End the session for good.
                await performSessionCleanup();
                safeRedirectToLogin();
                return Promise.reject(error);
            }

            // A hard refresh: the request may have gone out before Firebase restored the session.
            if (!auth.currentUser) await waitForAuthRestore();

            if (!auth.currentUser) {
                // Firebase dropped the user while the sign-in dialog is up: wait for them.
                if (isReauthPending()) return replayAfterReauth(requestConfig, error);
                // No session to refresh. Either the user is mid-logout
                // (and this is a stale in-flight response) or the request
                // was fired from a logged-out context. Do NOT cleanup or
                // redirect here — that would race with the UI and risk a
                // redirect loop. Just reject.
                return Promise.reject(error);
            }

            if (requestConfig.__authRetried) {
                // Already tried once with a fresh token and still 401 →
                // the session can't be refreshed (refresh token revoked,
                // account disabled, password changed). Ask the user to sign in.
                return replayAfterReauth(requestConfig, error);
            }

            try {
                if (!refreshPromise) {
                    refreshPromise = auth.currentUser
                        .getIdToken(true)
                        .finally(() => {
                            refreshPromise = null;
                        });
                }
                const freshToken = await refreshPromise;

                // Guard against logout racing with the refresh
                if (!auth.currentUser) {
                    return Promise.reject(error);
                }

                requestConfig.headers.Authorization = `Bearer ${freshToken}`;
                requestConfig.__authRetried = true;
                return axios(requestConfig);
            } catch (refreshErr) {
                if (isNetworkAuthError(refreshErr)) {
                    // Firebase's token server was out of reach (offline, a laptop just woke up): the session is
                    // fine. Keep the user signed in; the status bar explains, and the next request refreshes.
                    console.warn('axiosConfig: token refresh failed for want of a network', refreshErr);
                    if (typeof navigator === 'undefined' || navigator.onLine !== false) markServerUnreachable();
                    return Promise.reject(error);
                }
                console.error('axiosConfig: token refresh failed, asking the user to sign in again', refreshErr);
                return replayAfterReauth(requestConfig, error);
            }
        }

        // ── Scope-lifecycle freeze (409 GP-SCOPE-NOT-ACTIVE) ─────────────
        // A suspended/sunset org/game/client rejects content writes. This is a DEFINITIVE
        // rejection, not a transient in-flight conflict — do NOT retry it (the generic 409
        // retry below is for the idempotency layer's "still processing" case). Broadcast a
        // lifecycle-changed event so the UI re-resolves the freeze state and disables the
        // affordances that let this request through, then reject so the caller's catch runs.
        if (status === 409 && error.response?.data?.code === 'GP-SCOPE-NOT-ACTIVE') {
            try {
                window.dispatchEvent(new Event('gp-lifecycle-changed'));
            } catch (_) { /* non-browser / teardown — ignore */ }
            return Promise.reject(error);
        }
        // A READ of a suspended / sunset game's content is refused too (403 GP-SCOPE-NOT-ACTIVE): a frozen game is
        // no access, not read-only. Same re-resolve, so the page gives way to the lock card - but at most once per
        // few seconds, since every panel of the page is refused at once.
        if (isClosedScopeRead(error)) {
            try {
                notifyClosedScopeRead();
            } catch (_) { /* non-browser / teardown - ignore */ }
            return Promise.reject(error);
        }

        // ── Game-open refusal (403 GAME_ACCESS_SUSPENDED / GAME_ACCESS_DENIED) ─
        // The caller can no longer open the game this request named - e.g. an owner suspended them mid-session.
        // Re-read their games so the page is replaced by "your access is suspended" / "no access" rather than
        // every panel failing on its own. Definitive: never retried.
        if (gameAccessRefusal(error)) {
            try {
                rememberRefusedGame(signedInUserKey(), refusedGameOf(error));
                notifyGameAccessRefused();
            } catch (_) { /* non-browser / teardown - ignore */ }
            return Promise.reject(error);
        }

        // ── Any other 403: access may have changed under this tab ─────────
        // A permission refused that the page expected to have (it rendered the action) usually means someone just
        // changed this user's access. Ask the access refresh to re-check (it throttles itself); the caller still
        // gets the error.
        if (status === 403) {
            try {
                window.dispatchEvent(new Event(ACCESS_CHECK_EVENT));
            } catch (_) { /* non-browser / teardown - ignore */ }
            return Promise.reject(error);
        }

        const method = (requestConfig.method || '').toLowerCase();

        // ── GET that reached no server: try twice more, then say so ───────
        if (method === 'get') {
            if (axios.isCancel(error) || !isTransientReadFailure(error)) {
                return Promise.reject(error);
            }
            const readRetries = requestConfig.__readRetryCount || 0;
            if (readRetries < READ_RETRIES) {
                requestConfig.__readRetryCount = readRetries + 1;
                await sleep(RETRY_BASE_DELAY_MS * Math.pow(2, readRetries));
                return axios(requestConfig);
            }
            if (reachedNoServer(error)) markServerUnreachable({ retryAfterMs: parseRetryAfter(retryAfterHeader(error)) });
            return Promise.reject(error);
        }

        // ── Existing 5xx / 409 / network-error retry for mutating methods ─
        if (!MUTATING_METHODS.includes(method)) {
            return Promise.reject(error);
        }

        const retryCount = requestConfig.__retryCount || 0;
        if (retryCount >= MAX_RETRIES) {
            if (reachedNoServer(error)) markServerUnreachable({ retryAfterMs: parseRetryAfter(retryAfterHeader(error)) });
            return Promise.reject(error);
        }

        // Network errors, 5xx, and only the "still running" 409 (retryPolicy.js) - never a business conflict.
        if (!isRetryableFailure(error)) {
            return Promise.reject(error);
        }

        requestConfig.__retryCount = retryCount + 1;

        const delay = RETRY_BASE_DELAY_MS * Math.pow(2, retryCount);
        return new Promise((resolve) => setTimeout(resolve, delay))
            .then(() => axios(requestConfig));
    }
);
